import { useContext } from "react";
import { DesignContext } from "./DesignContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { generatePath } from "react-router-dom";
import { useProject } from "../Project";
import { useTeam } from "../Team";
import { useAppLoading } from "../AppLoading/useAppLoading";
import DesignStateService from "../../service/DesignStateService";
import DesignService from "../../service/DesignService";
import { ROUTES } from "../../constant/common.constant";
import { DesignFamily, DesignProduct } from "../../types";

export const useDesign = () => {
  const {
    designId,
    design,
    isDesignLoading,
    refetchDesign,
    updateDesignImages,
  } = useContext(DesignContext);
  const { refetchProject } = useProject();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsAppLoading } = useAppLoading();
  const { projectId } = useProject();
  const { teamId } = useTeam();

  const onDuplicate = async (redirectToEditor: boolean = true) => {
    if (!projectId || !teamId || !design) {
      return;
    }
    setIsAppLoading(true);
    const res = await DesignStateService.duplicateDesignState({
      designId: designId,
    });

    const duplicatedDesignState = res.data;
    let newDesignId = "";
    try {
      const res = await DesignService.addDesign(
        {
          projectId,
          designId: duplicatedDesignState.designId,
          name: `${t("general.copyOf")} ${design?.name}`,
        },
        duplicatedDesignState.design.product
      );
      await new Promise((resolve) => setTimeout(resolve, 3000));
      newDesignId = res?.data?.data?.id;
      await refetchDesign();
      await refetchProject();
      setIsAppLoading(false);
    } catch (e) {
      console.error(e);
      setIsAppLoading(false);
    } finally {
      if (projectId && teamId && design) {
        if (redirectToEditor && newDesignId) {
          navigate(
            generatePath(ROUTES.STUDIO, {
              projectId,
              designId: newDesignId,
              teamId,
            })
          );
        } else {
          navigate(generatePath(ROUTES.PROJECT, { projectId, teamId }));
        }
      }
      setIsAppLoading(false);
    }
  };

  const createDesign = async (
    family: DesignFamily,
    product: DesignProduct,
    newDesignName: string
  ) => {
    setIsAppLoading(true);
    try {
      const res = await DesignStateService.createDesignState({
        family,
        product,
      });

      const {
        data: { designId },
      } = res;

      await DesignService.addDesign(
        {
          projectId: projectId ?? "",
          designId,
          name: newDesignName,
        },
        product
      );

      await refetchDesign();
      await refetchProject();

      navigate(
        generatePath(ROUTES.STUDIO, {
          designId,
          projectId,
          teamId: teamId,
        })
      );
    } catch (error) {
      console.error(error);
      setIsAppLoading(false);
    } finally {
      setIsAppLoading(false);
    }
  };

  return {
    design,
    designId,
    isDesignLoading,
    updateDesignImages,
    onDuplicate,
    createDesign,
    refetchDesign,
  };
};
